import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Eye, EyeOff, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SortableList, SortableItem, PositionInput, moveTo } from "@/components/admin/Sortable";

const VideoRow = ({ v, onDelete, onToggle, handle, index, count, onMove }) => (
  <div data-testid={`admin-video-row-${v.id}`} className={`flex items-center gap-3 p-3 rounded-xl border border-[#2C3E55] ${v.is_active ? "bg-[#121E2B]" : "bg-[#121E2B]/40 opacity-60"}`}>
    {handle}
    <img src={`https://i.ytimg.com/vi/${v.youtube_id}/mqdefault.jpg`} alt="" className={`${v.type !== "portrait" ? "w-24 aspect-video" : "w-12 aspect-[9/16]"} object-cover rounded-lg bg-black`} />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-sm truncate">{v.title || <span className="text-[#F8FAFC]/50">Untitled</span>}</p>
      <a href={v.youtube_url} target="_blank" rel="noreferrer" className="text-xs text-[#8FA58A] hover:text-[#E2705A] truncate block">{v.youtube_url}</a>
    </div>
    {count > 1 && <PositionInput index={index} count={count} onMove={onMove} testId={`admin-video-position-${v.id}`} />}
    <span className="font-type text-[11px] uppercase px-2 py-0.5 rounded-full bg-[#2C3E55]">{v.type}</span>
    <button onClick={() => onToggle(v)} data-testid="admin-video-toggle-button" className="p-2 rounded-lg hover:bg-[#2C3E55]" title={v.is_active ? "Hide" : "Show"}>
      {v.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
    </button>
    <button onClick={() => onDelete(v)} data-testid="admin-video-delete-button" className="p-2 rounded-lg text-[#F8FAFC]/60 hover:text-[#E2705A] hover:bg-[#E2705A]/10">
      <Trash2 className="w-4 h-4" />
    </button>
  </div>
);

export const VideosManager = () => {
  const qc = useQueryClient();
  const { data: videos = [] } = useQuery({ queryKey: ["admin-videos"], queryFn: () => api.get("/admin/videos").then((r) => r.data) });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ youtube_url: "", title: "", type: "portrait" });
  const [busy, setBusy] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-videos"] });
    qc.invalidateQueries({ queryKey: ["videos"] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/videos", form);
      toast.success("Video tile added");
      setOpen(false);
      setForm({ youtube_url: "", title: "", type: "portrait" });
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (v) => {
    try {
      await api.delete(`/admin/videos/${v.id}`);
      toast.success("Video removed");
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggle = async (v) => {
    try {
      await api.put(`/admin/videos/${v.id}`, { is_active: !v.is_active });
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const landscape = videos.filter((v) => v.type === "landscape");
  const portraits = videos.filter((v) => v.type === "portrait");
  const feature = videos.filter((v) => v.type === "feature");
  const carousel = videos.filter((v) => v.type === "carousel");

  const reorder = async (list, ids) => {
    const byId = Object.fromEntries(list.map((v) => [v.id, v]));
    qc.setQueryData(["admin-videos"], (cur = []) => {
      const others = cur.filter((v) => !byId[v.id]);
      return [...others, ...ids.map((id, i) => ({ ...byId[id], order: i + 1 }))].sort((a, b) => a.order - b.order);
    });
    try {
      await api.put("/admin/videos/reorder", { ids });
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
      refresh();
    }
  };

  const SortedRows = ({ list, testId }) => (
    <SortableList items={list} onReorder={(ids) => reorder(list, ids)}>
      <div className="space-y-2" data-testid={testId}>
        {list.map((v, i) => (
          <SortableItem key={v.id} id={v.id}>
            {(handle) => <VideoRow v={v} index={i} count={list.length} handle={handle} onDelete={del} onToggle={toggle} onMove={(to) => reorder(list, moveTo(list.map((x) => x.id), i, to))} />}
          </SortableItem>
        ))}
      </div>
    </SortableList>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#F8FAFC]/70">Portrait tiles slide across the landing page. The active landscape video sits under the title. The feature video sits below the sign-up form (hidden if none is active or the link is broken). Drag the grip or type a # to set the order.</p>
        <button onClick={() => setOpen(true)} data-testid="admin-add-video-button" className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#E2705A] hover:bg-[#D35D46] font-type font-bold uppercase tracking-wider text-xs transition-colors">
          <Plus className="w-4 h-4" /> Add video tile
        </button>
      </div>
      <section>
        <h3 className="font-type text-xs uppercase tracking-widest text-[#8FA58A] mb-3">Landscape (hero)</h3>
        <SortedRows list={landscape} testId="admin-videos-landscape" />
      </section>
      <section>
        <h3 className="font-type text-xs uppercase tracking-widest text-[#8FA58A] mb-3">Feature video (below sign-up)</h3>
        {feature.length ? <SortedRows list={feature} testId="admin-videos-feature" /> : <p className="text-xs text-[#F8FAFC]/50" data-testid="admin-feature-empty">No feature video — the slot is hidden on the site.</p>}
      </section>
      <section>
        <h3 className="font-type text-xs uppercase tracking-widest text-[#8FA58A] mb-3">Landscape carousel — below WTF ({carousel.length})</h3>
        {carousel.length ? <SortedRows list={carousel} testId="admin-videos-carousel" /> : <p className="text-xs text-[#F8FAFC]/50" data-testid="admin-carousel-empty">No carousel videos — the strip is hidden on the site.</p>}
      </section>
      <section>
        <h3 className="font-type text-xs uppercase tracking-widest text-[#8FA58A] mb-3">Portrait tiles ({portraits.length})</h3>
        <SortedRows list={portraits} testId="admin-videos-portrait" />
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-video-modal" className="bg-[#1B2A41] border-[#2C3E55] text-[#F8FAFC]">
          <DialogHeader>
            <DialogTitle className="font-type">Add video tile</DialogTitle>
            <DialogDescription className="text-[#F8FAFC]/60">Paste any YouTube link (Shorts, watch, or youtu.be).</DialogDescription>
          </DialogHeader>
          <form onSubmit={add} className="space-y-4">
            <div className="space-y-1.5">
              <Label className="font-type text-xs uppercase tracking-wider text-[#8FA58A]">YouTube URL</Label>
              <Input data-testid="admin-video-url-input" required value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://www.youtube.com/shorts/..." className="bg-[#121E2B] border-[#2C3E55]" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-type text-xs uppercase tracking-wider text-[#8FA58A]">Title (optional)</Label>
              <Input data-testid="admin-video-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#121E2B] border-[#2C3E55]" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-type text-xs uppercase tracking-wider text-[#8FA58A]">Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {["portrait", "landscape", "feature", "carousel"].map((t) => (
                  <button type="button" key={t} data-testid={`admin-video-type-${t}`} onClick={() => setForm({ ...form, type: t })} className={`flex-1 h-10 rounded-full border text-sm font-semibold capitalize transition-colors ${form.type === t ? "bg-[#E2705A] border-[#E2705A]" : "border-[#2C3E55] hover:border-[#8FA58A]"}`}>
                    {t === "portrait" ? "Portrait tile" : t === "landscape" ? "Landscape (hero)" : t === "feature" ? "Feature (below sign-up)" : "Landscape carousel"}
                  </button>
                ))}
              </div>
              {form.type === "landscape" && <p className="text-[11px] text-[#F8FAFC]/50">Adding a landscape video replaces the current hero video.</p>}
              {form.type === "feature" && <p className="text-[11px] text-[#F8FAFC]/50">Adding a feature video replaces the current one under the sign-up form.</p>}
            </div>
            <button type="submit" disabled={busy} data-testid="admin-video-submit-button" className="w-full h-11 rounded-full bg-[#E2705A] hover:bg-[#D35D46] disabled:opacity-60 font-type font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
